import { v4 as uuidv4 } from 'uuid';

import {
  ContextVariable,
  DynamicListOption,
} from 'src/types/actionBuilderApiTypes';

export interface ContextVariableOptionWithIndent {
  // disable parent items that should not be selectable
  disabled: boolean;
  // label including the parent CV name
  fullLabel?: string;
  // id of the context variable or a temporary id for parent items
  id: string;
  // indent level of the item in the object
  indent: number;
  // whether the item is undefined
  isDefined?: boolean;
  // label of the key
  label: string;
  type: ContextVariable['context_variable_type'];
}

/**
 * Both dynamic-list types store their payload the same way; they differ only in
 * whether the visitor can pick one item or several. Use this wherever the
 * question is "is this a dynamic list at all" -- particularly in the `!==`
 * filters that exclude dynamic lists from a picker, which would otherwise start
 * silently admitting the multi-select type.
 */
export const isDynamicListType = (
  type?: ContextVariable['context_variable_type'] | null,
) => type === 'DYNAMIC_LIST' || type === 'DYNAMIC_LIST_MULTI_SELECT';

/**
 * Multi-select dynamic lists are consumable only as a whole object -- in a code
 * step, printed into the conversation, or piped into an action body. They have
 * no dot-notation child references.
 */
export const isMultiSelectDynamicListType = (
  type?: ContextVariable['context_variable_type'] | null,
) => type === 'DYNAMIC_LIST_MULTI_SELECT';

/**
 * Types to disable in pickers for surfaces that cannot consume a multi-select
 * dynamic list. The server rejects these bindings at runtime; greying the type
 * out here means the admin never gets that far. Module-level so the identity is
 * stable across renders.
 */
export const MULTI_SELECT_DYNAMIC_LIST_DISABLED_TYPES: ContextVariable['context_variable_type'][] =
  ['DYNAMIC_LIST_MULTI_SELECT'];

export const hasDynamicListChildren = (
  cv: ContextVariable | DynamicListOption,
) => {
  return Boolean(
    'configuration_fields' in cv &&
    cv.configuration_fields?.dynamic_list_config?.context_variables &&
    cv.configuration_fields.dynamic_list_config.context_variables.length !== 0,
  );
};

/**
 * Flattens the dynamic context variables and formats them in such a way that they can be used by the rich text editor mention component.
 */
export function flattenDynamicContextVariables(
  contextVariables: ContextVariable[],
  shouldIncludeDynamicListContextVariableChildren = true,
): ContextVariableOptionWithIndent[] {
  const flattened: ContextVariableOptionWithIndent[] = [];

  for (const cv of contextVariables) {
    const isDynamicList = isDynamicListType(cv.context_variable_type);
    // Multi-select dynamic lists have no dot-notation children: they are only
    // referenceable as a whole object, so the parent stays selectable and the
    // schema tree is never offered as references.
    const hasReferenceableChildren =
      isDynamicList && !isMultiSelectDynamicListType(cv.context_variable_type);

    const flattenedCV: ContextVariableOptionWithIndent = {
      disabled: hasReferenceableChildren && hasDynamicListChildren(cv),
      fullLabel: cv.context_variable_name,
      id: cv.context_variable_id,
      indent: 0,
      label: cv.context_variable_name,
      type: cv.context_variable_type,
    };
    flattened.push(flattenedCV);

    if (
      shouldIncludeDynamicListContextVariableChildren &&
      hasReferenceableChildren &&
      cv.configuration_fields?.dynamic_list_config?.context_variables
    ) {
      flattened.push(
        ...formatDynamicListChildOptions({
          indent: 1,
          options:
            cv.configuration_fields.dynamic_list_config.context_variables,
          parentCVName: cv.context_variable_name,
        }),
      );
    }
  }

  return flattened;
}

export function formatDynamicListChildOptions({
  indent = 0,
  options,
  parentCVName,
}: {
  indent?: number;
  options: DynamicListOption[];
  parentCVName: string;
}): ContextVariableOptionWithIndent[] {
  const flattened: ContextVariableOptionWithIndent[] = [];

  for (const option of options) {
    const flattenedOption: ContextVariableOptionWithIndent = {
      disabled: !option.context_variable_id,
      fullLabel: option.context_variable_id
        ? formatNestedListOption(parentCVName, option.context_variable_id)
        : undefined,
      id: option.context_variable_id || uuidv4(),
      indent,
      label: option.context_variable_name,
      type: 'DYNAMIC_LIST',
    };
    flattened.push(flattenedOption);
    if (option.options) {
      const nestedCVs = formatDynamicListChildOptions({
        indent: indent + 1,
        options: option.options,
        parentCVName,
      });
      flattened.push(...nestedCVs);
    }
  }
  return flattened;
}

/**
 * Returns a tuple of [id, suffix]
 * For example, `context_variable_id/./key0.key1` -> ['context_variable_id', 'key0.key1']
 */
export function splitDynamicContextVariableId(id: string) {
  return id.split(/\/\.\//g);
}

export function formatNestedListOption(parentCVName: string, id: string) {
  const suffix = splitDynamicContextVariableId(id).slice(1);

  return `${parentCVName}.${suffix}`;
}

/**
 * Resolves a context variable ID (including dynamic list child IDs in the
 * `parentCvId/./nestedKey` format) to a display name like
 * `parentCvName.nestedKey`. Nested `context.<cvId>` segments inside the
 * suffix are also resolved to their CV name. Returns `undefined` when no
 * matching parent CV is found.
 */
export function getContextVariableName(
  contextVariableId: string,
  contextVariables: ContextVariable[],
): string | undefined {
  const exactMatch = contextVariables.find(
    cv =>
      cv.context_variable_id === contextVariableId ||
      `[[${cv.context_variable_id}]]` === contextVariableId,
  );
  if (exactMatch) {
    return exactMatch.context_variable_name;
  }

  const parts = splitDynamicContextVariableId(contextVariableId);
  if (parts.length > 1) {
    const [parentCvId, ...rest] = parts;
    const parentCv = contextVariables.find(
      cv => cv.context_variable_id === parentCvId,
    );
    if (parentCv) {
      const nestedKey = rest
        .join('.')
        .replace(/context\.(.+)$/, (_match, cvId: string) => {
          const cv = contextVariables.find(c => c.context_variable_id === cvId);
          return `context.${cv?.context_variable_name ?? cvId}`;
        });
      return `${parentCv.context_variable_name}.${nestedKey}`;
    }
  }

  return undefined;
}

export function convertDynamicListCVChildrenToListOptions(
  dynamicListCV: ContextVariable,
  color?: string,
) {
  const childrenOfSelectedCV =
    dynamicListCV.configuration_fields?.dynamic_list_config?.context_variables;
  if (!childrenOfSelectedCV) {
    return null;
  }
  const formattedOptions = formatDynamicListChildOptions({
    options: childrenOfSelectedCV,
    parentCVName: dynamicListCV.context_variable_name,
  });
  // options without fullLabel are not selectable because they point to a non-primitive value, so filter them out
  const validOptions = formattedOptions.filter(option => option.fullLabel);

  return validOptions.map(option => {
    const label = '$' + option.fullLabel;
    const [, value] = splitDynamicContextVariableId(option.id);
    return {
      color,
      label,
      value,
    };
  });
}
